import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { ModalHeader, useDebounce, TextInput, CheckboxInput, Dropdown } from "@flatfile/design-system";
import { useEffect, useState, useRef, useMemo } from "react";
import { Radio } from "../../../common/UI/Inputs/Radio.js";
import "lodash.find";
import "../../../common/UI/Loaders/Activity.js";
import { FetchStatus } from "../../../utils/buffer/types.js";
import { useTranslation } from "react-i18next";
import "../../Cell/Lookup/Styles.js";
import { useLookupOptions, MAX_OPTIONS } from "../../Cell/Lookup/useLookupOptions.js";
import "lodash.isequal";
import "../../Cell/CellContainer.js";
import "../../Cell/Styles.js";
import { useDropdown } from "../../../hooks/useDropdown.js";
import { EColumnType } from "../../../types/Column.js";
import { ModalText, TextInputWrapper, HintWrapper, CheckboxContainer, BooleanGroup, DropdownWrapper, DropdownMessage } from "../Styles.js";
const NullContentHeader = (props) => {
  const { t } = useTranslation();
  const { columnName, count, ignoreFilter, handleOnSearch } = props;
  useEffect(() => {
    handleOnSearch("");
  }, [ignoreFilter]);
  const formattedCount = count == null ? void 0 : count.toLocaleString();
  const noResults = count === 0;
  const hideCounts = count === "";
  const heading = noResults ? t(
    "sheet.table.contextMenu.findAndReplace.modal.replaceEmpty.heading.noEmptyValues"
  ) : hideCounts ? t(
    "sheet.table.contextMenu.findAndReplace.modal.replaceEmpty.heading.addValues",
    {
      columnName
    }
  ) : formattedCount ? t(
    "sheet.table.contextMenu.findAndReplace.modal.replaceEmpty.heading.addValuesToCount",
    {
      count,
      formattedCount,
      columnName
    }
  ) : t(
    "sheet.table.contextMenu.findAndReplace.modal.replaceEmpty.heading.addValuesToAll",
    {
      columnName
    }
  );
  const description = noResults ? t(
    "sheet.table.contextMenu.findAndReplace.modal.replaceEmpty.description.noEmptyValues"
  ) : hideCounts ? t(
    "sheet.table.contextMenu.findAndReplace.modal.replaceEmpty.description.addValues",
    {
      columnName
    }
  ) : formattedCount ? t(
    "sheet.table.contextMenu.findAndReplace.modal.replaceEmpty.description.addValuesToCount",
    {
      count,
      formattedCount,
      columnName
    }
  ) : t(
    "sheet.table.contextMenu.findAndReplace.modal.replaceEmpty.description.addValuesToAll",
    {
      columnName
    }
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(ModalHeader, { children: heading }),
    /* @__PURE__ */ jsx(ModalText, { children: description })
  ] });
};
const FindValueModalContent = ({
  handleOnSearch,
  columnName,
  count,
  setDisableSubmit,
  columnType,
  toggleExactMatchOnly,
  exactMatchesOnly,
  ignoreFilter
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const debouncedInputValue = useDebounce(inputValue, 500);
  const touched = useRef();
  useEffect(() => {
    if (touched.current || inputValue.length > 0) {
      touched.current = true;
      handleOnSearch(inputValue);
    }
  }, [debouncedInputValue, exactMatchesOnly, ignoreFilter]);
  const hintMessage = count ? t("sheet.table.contextMenu.findAndReplace.modal.searchResultsCount", {
    count
  }) : t("sheet.table.contextMenu.findAndReplace.modal.searchForEmpty");
  const toggleCheckbox = () => {
    if (columnType === "string") {
      toggleExactMatchOnly();
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(ModalHeader, { children: t("sheet.table.contextMenu.findAndReplace.modal.heading", {
      columnName
    }) }),
    /* @__PURE__ */ jsx(ModalText, { children: columnType === EColumnType.string ? t(
      "sheet.table.contextMenu.findAndReplace.modal.descriptionPartialAndExact"
    ) : t(
      "sheet.table.contextMenu.findAndReplace.modal.descriptionExactOnly"
    ) }),
    /* @__PURE__ */ jsxs(TextInputWrapper, { children: [
      /* @__PURE__ */ jsx(
        TextInput,
        {
          autoFocus: true,
          label: t(
            "sheet.table.contextMenu.findAndReplace.modal.searchForText"
          ),
          value: inputValue,
          onChange: (value) => setInputValue(value),
          onBeforeInput: () => setDisableSubmit == null ? void 0 : setDisableSubmit(true)
        }
      ),
      count !== "" && /* @__PURE__ */ jsx(HintWrapper, { children: hintMessage }),
      columnType === EColumnType.string && /* @__PURE__ */ jsx(CheckboxContainer, { children: /* @__PURE__ */ jsx(
        CheckboxInput,
        {
          id: "exact-matches-only",
          onChange: toggleCheckbox,
          checked: exactMatchesOnly,
          label: t(
            "sheet.table.contextMenu.findAndReplace.modal.exactMatchesOnly"
          ),
          smallLabel: true
        }
      ) })
    ] })
  ] });
};
const OptionsDropdown = ({
  autoFocus,
  options,
  onChange
}) => {
  const { t } = useTranslation();
  const {
    filteredOptionsList,
    fetchStatus,
    isFunctionType,
    inputValue,
    onInputChange
  } = useLookupOptions({ options });
  const { dropdownOpen, onOpenChange } = useDropdown({});
  const hasMax = filteredOptionsList.length === MAX_OPTIONS;
  const hasNone = filteredOptionsList.length === 0;
  const message = useMemo(() => {
    if (!isFunctionType || fetchStatus !== FetchStatus.success)
      return "";
    if (hasMax) {
      return t(
        "sheet.table.contextMenu.findAndReplace.modal.replaceEnum.moreOptions"
      );
    }
    if (hasNone) {
      return t(
        "sheet.table.contextMenu.findAndReplace.modal.replaceEnum.noMatchesFound"
      );
    }
    return "";
  }, [hasMax, hasNone, isFunctionType, fetchStatus]);
  const showHasMaxMessage = !!message && hasMax && dropdownOpen;
  const showHasNoneMessage = !!message && hasNone;
  const dropdownProps = {
    maxWidth: 464,
    maxPopoverWidth: "464px",
    minWidth: 464,
    autoFocus: false,
    searchable: isFunctionType,
    loading: fetchStatus === FetchStatus.loading,
    error: fetchStatus === FetchStatus.failed,
    optionsList: filteredOptionsList,
    label: t(
      "sheet.table.contextMenu.findAndReplace.modal.replaceEnum.replaceWith"
    ),
    // eslint-disable-next-line
    inputValue,
    // eslint-disable-next-line
    onInputChange,
    onSelectionChange: onChange,
    onOpenChange
  };
  return /* @__PURE__ */ jsxs(
    DropdownWrapper,
    {
      hasMaxMessage: showHasMaxMessage,
      hasNoneMessage: showHasNoneMessage,
      children: [
        (showHasMaxMessage || showHasNoneMessage) && /* @__PURE__ */ jsx(DropdownMessage, { none: hasNone, children: message }),
        /* @__PURE__ */ jsx(Dropdown, { className: "ff-ds", ...dropdownProps })
      ]
    }
  );
};
const ReplaceValueContent = ({
  handleBooleanChange,
  handleChange,
  noResults,
  options,
  replaceNulls,
  replaceValue,
  showReplaceOptions,
  type
}) => {
  const { t } = useTranslation();
  const hintMessage = noResults ? t(
    "sheet.table.contextMenu.findAndReplace.modal.replaceValue.noMatchesFound"
  ) : t(
    "sheet.table.contextMenu.findAndReplace.modal.replaceValue.leavingBlank"
  );
  if (!showReplaceOptions) {
    return null;
  }
  if (type === EColumnType.boolean) {
    return /* @__PURE__ */ jsxs(BooleanGroup, { children: [
      /* @__PURE__ */ jsx(
        Radio,
        {
          autoFocus: replaceNulls,
          id: "true",
          label: "True",
          value: "true",
          onChange: handleBooleanChange
        }
      ),
      /* @__PURE__ */ jsx(
        Radio,
        {
          id: "false",
          label: "False",
          value: "false",
          onChange: handleBooleanChange
        }
      )
    ] });
  }
  if (type === EColumnType.enum && options) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        OptionsDropdown,
        {
          autoFocus: replaceNulls,
          options,
          onChange: handleChange
        }
      ),
      noResults && /* @__PURE__ */ jsx(HintWrapper, { error: noResults, children: hintMessage })
    ] });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      TextInput,
      {
        autoFocus: replaceNulls,
        label: t(
          "sheet.table.contextMenu.findAndReplace.modal.replaceValue.replaceWith"
        ),
        value: replaceValue,
        onChange: handleChange,
        isDisabled: noResults
      }
    ),
    !replaceNulls && /* @__PURE__ */ jsx(HintWrapper, { error: noResults, children: hintMessage })
  ] });
};
export {
  FindValueModalContent,
  NullContentHeader,
  ReplaceValueContent
};
